import styled, { css } from 'styled-components';

import { Text } from '+/components/Text/Text.styles';
import { Content } from '+/styles/styledComponents/Form.styles';
import { PaymentMethodsWrapper } from '+/components/PaymentMethodLogos/PaymentMethodLogos.styles';
import { textH1 } from '+/styles/reusable/textStyles';
import * as sharedStyles from '+/styles/reusable/inputStyles';

export const DonationAskForm = styled.div<{ isPadded?: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;

  h1 {
    ${textH1}
    letter-spacing: -0.02em;
    padding: 1rem 0;

    @media (min-width: ${props => props.theme.breakpoints.lg}) {
      padding: 0 3rem;
    }
  }

  ${Content} {
    flex: 1;

    padding-left: 1.5rem;
    padding-right: 1.5rem;

    ${props =>
      !props.isPadded &&
      css`
        clip-path: none;
        padding-top: 0;
      `}

    p > em {
      font-family: var(--font-tertiary);
      font-size: 1.25rem;
      margin: 0.125rem 0;
      display: inline-block;

      @media (min-width: ${props => props.theme.breakpoints.lg}) {
        margin: 0.5rem 0;
      }
    }

    @media (min-width: ${props => props.theme.breakpoints.lg}) {
      padding-left: 3rem;
      padding-right: 3rem;
    }
  }

  ${PaymentMethodsWrapper} {
    background: var(--color-white);
  }

  ${Text} {
    max-width: 30rem;
    padding: ${props => (props.isPadded ? '3.5rem' : '0')} 0 2rem 0;

    p {
      margin-bottom: 1.5rem;
    }

    /* Buttons rendered by markdown :button directives, scoped here so the amount tiles keep their own styling */
    button {
      margin: 0.5rem 0;
      background: var(--color-theme-light);
      width: 100%;

      :hover {
        background: var(--color-theme-dark);
      }

      @media (min-width: ${props => props.theme.breakpoints.lg}) {
        width: auto;
      }
    }
  }
`;

export const DonationAmountOptions = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  gap: 1rem;
  padding: 0 0 1.25rem 0;
  width: 100%;
  max-width: 30rem;
`;

/* Amount tiles mirror the donation form (everyaction-themes _select-amount.scss):
   cut corner drawn by a clip-path on :before that animates closed when selected */
const amountTile = css`
  position: relative;
  isolation: isolate;
  cursor: pointer;
  min-width: 0;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  container-type: inline-size;
  font-family: var(--font-secondary);
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.015em;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
  color: var(--color-neutral-500);
  transition: color 0.5s var(--ease-out-expo);

  input[type='radio'] {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  :before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: var(--color-neutral-250);
    clip-path: polygon(0 0, 100% 0, 100% calc(100% - 1.5rem), calc(100% - 1.5rem) 100%, 0 100%);
    transition: background-color 0.5s var(--ease-out-expo), clip-path 0.5s var(--ease-out-expo);
  }

  :hover:before,
  &:has(input[type='radio']:focus-visible):before {
    background: var(--color-white);
  }

  &:has(input[type='radio']:focus-visible) {
    outline: 2px solid var(--color-theme);
    outline-offset: 2px;
  }
`;

const selectedTile = css`
  color: var(--color-white);

  :before,
  :hover:before,
  &:has(input[type='radio']:focus-visible):before {
    background: var(--color-theme);
    clip-path: polygon(0 0, 100% 0, 100% 100%, 100% 100%, 0 100%);
  }
`;

export const AmountLabel = styled.label<{ isSelected?: boolean }>`
  ${amountTile}
  ${props => props.isSelected && selectedTile}
`;

/* Sized on a child of the tile: container units resolve against the nearest ANCESTOR
   container, so a cqi font-size on the tile itself would fall back to viewport units */
export const AmountLabelContent = styled.span<{ hasSuffix?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  /* Fallback first: browsers without container queries ignore the cqi clamp below */
  font-size: 1.75rem;
  font-size: ${props =>
    props.hasSuffix ? 'clamp(0.875rem, 21cqi, 1.75rem)' : 'clamp(0.875rem, 26cqi, 1.75rem)'};
`;

/* em-based so they scale with the tile's container-responsive font size (1rem/1.75rem on the donation pages) */
export const AmountUnit = styled.span`
  font-size: 0.575em;
  margin-block-start: -0.25em;
`;

export const AmountSuffix = styled.span`
  font-size: 0.575em;
  text-transform: lowercase;
  margin-block-end: -0.5em;
`;

export const OtherLabelText = styled.span`
  font-size: 1rem;
  font-size: clamp(0.75rem, 15cqi, 1rem);
  transition: opacity 0.5s var(--ease-out-expo);
`;

export const OtherAmountPrefix = styled.span`
  position: absolute;
  left: 0;
  width: 2rem;
  font-size: 1.25rem;
  line-height: 1;
  opacity: 0;
  transform: translateY(0.5em);
  transition: all 0.5s var(--ease-out-expo);
`;

export const OtherAmountInput = styled.input`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 0;
  opacity: 0;
  padding: 0;
  border: none;
  border-block-end: 0.1875rem solid var(--color-theme);
  background: var(--color-neutral-100);
  color: var(--color-neutral-600);
  font-family: var(--font-secondary);
  font-size: 1.75rem;
  font-size: clamp(1rem, 21cqi, 1.75rem);
  font-weight: 400;
  letter-spacing: 0;
  text-align: center;
  transition: all 0.5s var(--ease-out-expo);
  transition-property: opacity, width, padding;

  :focus {
    border-bottom-color: var(--color-theme);
    outline: none;
  }
`;

export const OtherAmountLabel = styled.label<{ isSelected?: boolean }>`
  ${amountTile}

  ${props =>
    props.isSelected &&
    css`
      ${selectedTile}

      ${OtherLabelText} {
        opacity: 0;
      }

      ${OtherAmountPrefix} {
        opacity: 1;
        transform: translateY(0);
        transition-delay: 0.25s;
      }

      ${OtherAmountInput} {
        width: calc(100% - 2rem);
        opacity: 1;
      }
    `}
`;

export const MonthlyGiftLabel = styled.label`
  display: block;
  width: 100%;
  max-width: 30rem;
  text-align: left;
`;

export const MonthlyGiftCheckbox = styled.input`
  ${sharedStyles.checkbox};
`;

export const MonthlyGiftLabelText = styled.span`
  ${sharedStyles.checkboxLabelText};
`;

export const SubmitLink = styled.a`
  display: inline-block;
  margin-top: 2.5rem;
`;

const footerLink = css`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-secondary);
  font-weight: 700;
  color: var(--color-neutral-800);
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }
`;

export const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.5rem;

  /* && outranks the border reset in footerLink */
  && > * + * {
    border-left: 1px solid var(--color-neutral-350);
    margin-left: 1rem;
    padding-left: 1rem;
  }
`;

export const MoreGivingOptionsLink = styled.a`
  ${footerLink}
`;

export const SkipButton = styled.button`
  ${footerLink}
`;
